/**
 * View Button Component
 * Reusable button for highlighting and viewing elements in the Forge Viewer
 */

import React from 'react';
import { useViewerStore } from '@/store/viewerStore';
import { ViewerService } from '@/services/core/viewerTools';

interface ViewButtonProps {
  dbId: number | number[];
  label?: string;
  showLabel?: boolean;
  size?: 'small' | 'normal';
  isolate?: boolean;
  color?: THREE.Vector4;
  surroundingDbIds?: number[]; // For future use with surrounding elements
  surroundingColor?: THREE.Vector4;
  className?: string;
  onView?: () => void;
}

export function ViewButton({
  dbId,
  label = 'View',
  showLabel = true,
  size = 'small',
  isolate = true,
  color,
  surroundingDbIds,
  surroundingColor,
  className = '',
  onView
}: ViewButtonProps) {
  const { viewer } = useViewerStore();

  const handleView = () => {
    if (!viewer) return;

    const dbIds = Array.isArray(dbId) ? dbId : [dbId];
    
    // If surrounding elements are provided, use special coloring
    if (surroundingDbIds && surroundingDbIds.length > 0 && !Array.isArray(dbId)) {
      ViewerService.colorSurroundingElements(
        viewer,
        dbId as number,
        surroundingDbIds,
        color,
        surroundingColor
      );
    } else {
      // Standard highlighting
      ViewerService.highlightElements(viewer, dbIds, color, isolate);
    }

    // Call optional callback
    if (onView) {
      onView();
    }
  };

  const buttonClass = size === 'small' 
    ? 'btn-small px-2 py-1 text-xs' 
    : 'px-3 py-1.5 text-sm';

  return (
    <button
      className={`inline-flex items-center gap-1 bg-custom-navy hover:bg-custom-dark text-white rounded transition-colors ${buttonClass} ${className}`}
      onClick={handleView}
      title={`View element${Array.isArray(dbId) ? 's' : ''} in 3D viewer`}
    >
      <span>👁️</span>
      {showLabel && <span>{label}</span>}
    </button>
  );
}